import React from "react"
import { css, cx } from "@emotion/css"

import Checkbox from "@material-ui/core/Checkbox"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import Typography from "@material-ui/core/Typography"
import { Surface } from "components/Surface"
import { useGetSemanticName } from "../utils"

type Column = {
  id: string
  label: string
  semanticCode?: string
  type?: string
  checked: boolean
  disabled?: boolean
}

type Props = {
  columns: Column[]
  onChange: (id: string) => void
  label?: string
}

export const TableColumnPicker = ({ columns, onChange, label }: Props) => {
  const sortedColumns = [...columns].sort((a, b) =>
    a.label.localeCompare(b.label),
  )

  return (
    <Surface variant="custom" size="custom">
      {label ? (
        <Typography variant="body1" className={styles.title}>
          {label}
        </Typography>
      ) : null}
      <Surface
        variant="custom"
        size="custom"
        display="grid"
        gridAutoFlow="row"
        gridRowGap="0"
      >
        <Table columns={sortedColumns} onChange={onChange} />
      </Surface>
    </Surface>
  )
}

type TableProps = {
  columns: Column[]
  onChange: (id: string) => void
}

function Table(props: TableProps) {
  const { columns, onChange } = props
  const { getSemanticName } = useGetSemanticName()
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <tbody>
          {columns.map((col, idx) => (
            <tr role="row" key={`${col.id}.${idx}`}>
              <td>
                <FormControlLabel
                  disabled={col.disabled}
                  label={col.label}
                  classes={{ root: styles.formLabel }}
                  control={
                    <Checkbox
                      color="primary"
                      checked={Boolean(col.checked)}
                      onChange={(_) => onChange(col.id)}
                      className={styles.checkbox}
                    />
                  }
                />
              </td>
              <td className={cx({ [styles.disabled]: col.disabled })}>
                {getSemanticName(col.semanticCode)}
              </td>
              {!!col.type && (
                <td className={cx({ [styles.disabled]: col.disabled })}>
                  {col.type}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const styles = {
  title: css`
    font-size: 0.8125rem;
    position: fixed;
  `,
  subtitle: css`
    margin: 1rem 0;
  `,
  checkbox: css`
    padding: 2px 9px;
    margin-left: -11px;
  `,
  scroll: css`
    height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
  `,
  table: css`
    border-spacing: 0;
    border-collapse: collapse;
    width: 100%;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      font-size: 13px;
      font-weight: normal;
      width: 40%;
      padding-right: 4px;
      padding-left: 4px;

      :last-child {
        border-right: 0;
      }
      :first-child {
        padding-left: 1px;
      }

      &.right {
        text-align: right;
      }
      &.left {
        text-align: left;
      }
    }

    th {
      font-size: 12px;
      font-weight: bold;
    }
  `,
  formLabel: css`
    margin-right: 0;
    margin-left: 0;
  `,
  disabled: css`
    color: var(--grey);
  `,
}
